import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import type { ListSharedCompanyColorsResponse } from '@customer/gen_proto/api_proto/v1/color_pb'
import { ListSharedCompanyColorsRequestSchema } from '@customer/gen_proto/api_proto/v1/color_pb'
import type { GetColorSimulationByShareLinkResponse } from '@customer/gen_proto/api_proto/v1/customer_service_pb'
import {
  CustomerService,
  GetColorSimulationByShareLinkRequestSchema,
} from '@customer/gen_proto/api_proto/v1/customer_service_pb'
import { transport } from '@customer/request/client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ProcessImageRequest } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import { sortByColorCodeWithinManufacturer } from '@ai-color-modules/simulation/ColorPalette/colorSort'

export const client = createClient(CustomerService, transport)

const QUERY_KEY = ['colorSimulationByShareLink'] as const

/** 共有リンクからカラーシミュレーション情報を取得する */
export function useFetchColorSimulation(enabled = true) {
  return useQuery<GetColorSimulationByShareLinkResponse>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      return await client.getColorSimulationByShareLink(
        create(GetColorSimulationByShareLinkRequestSchema),
      )
    },
    enabled,
  })
}

/** 施主がカラーシミュレーションを実行する */
export function useProcessImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: ProcessImageRequest) => {
      return await client.processImage(request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

const SHARED_COLORS_QUERY_KEY = ['listSharedCompanyColors'] as const

/**
 * 共有リンクから企業が指定したエリア別の色一覧を取得する.
 * レスポンスは `areas: SharedAreaColors[]`（サーバがプリセット + 履歴色を解決済み）。
 * area_key はベース = canonical英語名、カスタム = sub_areas の index 文字列.
 * 各エリアの colors を同一メーカー内で code 昇順（自然順）ソートして返す.
 */
export function useFetchSharedCompanyColors(enabled = true) {
  return useQuery<ListSharedCompanyColorsResponse>({
    queryKey: SHARED_COLORS_QUERY_KEY,
    queryFn: async () => {
      return await client.listSharedCompanyColors(create(ListSharedCompanyColorsRequestSchema))
    },
    enabled,
    select: response => ({
      ...response,
      areas: response.areas.map(area => ({
        ...area,
        colors: sortByColorCodeWithinManufacturer(
          area.colors,
          c => c.code,
          c => c.manufacturer,
        ),
      })),
    }),
  })
}

/** 施主が履歴のタイトルを更新する（issue #2370）。空文字列は未設定に戻す */
export function useUpdateTitle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { imageId: number; title: string }) => {
      return await client.updateColorSimulationImageTitle({
        imageId: BigInt(params.imageId),
        title: params.title,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

/** 施主がお気に入りを切り替える */
export function useToggleFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { imageId: number; favorited: boolean }) => {
      return await client.toggleColorSimulationImageFavorite({
        imageId: BigInt(params.imageId),
        favorited: params.favorited,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}
